import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useGlobalState } from '@/state'
import { requestEzoicAds } from '@/utils/ezoicAds'
import { trackAdClick, useAdImpression } from '@/utils/adTelemetry'
import useQuery from '@/hooks/useQuery'
import { SpeakerphoneIcon, SparklesIcon, PlayIcon, PauseIcon } from '@heroicons/react/solid'


const defaultEduAds = [
  {
    name: 'Voice Acting 101',
    imageUrl: 'https://img.closingcredits.com/image_uploads/64/64va101.png'
  },
  {
    name: 'Audio Engineering for Voice Actors',
    imageUrl: 'https://img.closingcredits.com/image_uploads/56/56tony_ae4va.png'
  }
]

const ProjectAds = (props) => {
  return <>
    <div className='flex flex-col gap-y-1'>
      <SidebarEducationAd />
      <SidebarUserAd />
    </div>
  </>
}

const MobileNouscraftAd = () => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [hasTrackedClick, setHasTrackedClick] = useState(false)
  const audioRef = useRef(null)
  const impressionRef = useAdImpression('nouscraft', 'mobile', 'audiobook-trailer')
  const { putpostRequest } = useQuery()

  const handlePlayAudio = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!audioRef.current) {
      audioRef.current = new Audio('https://images.castingcall.club/nouscraft-commercial.mp3')
      audioRef.current.addEventListener('ended', () => setIsPlaying(false))
      audioRef.current.addEventListener('loadedmetadata', () => {
        setDuration(audioRef.current.duration)
      })
      audioRef.current.addEventListener('timeupdate', () => {
        setCurrentTime(audioRef.current.currentTime)
      })
    }
    
    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      // Track the click only once per session
      if (!hasTrackedClick) {
        putpostRequest('/api/v3/auggers/track_nouscraft_click', 'POST', {}, (err, data) => {
          if (!err) {
            setHasTrackedClick(true)
          }
        })
      }
      trackAdClick('nouscraft', 'mobile', 'audiobook-trailer')
      audioRef.current.play()
      setIsPlaying(true)
    }
  }

  const handleSeek = (e) => {
    e.stopPropagation()
    const newTime = parseFloat(e.target.value)
    setCurrentTime(newTime)
    if (audioRef.current) {
      audioRef.current.currentTime = newTime
    }
  }

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  // Shows to EVERYONE, ad-free members included — owner decision 2026-08-13,
  // extending the education carve-out below. "Ad-free" buys freedom from
  // third-party programmatic ads (Ezoic on web, AdMob in the app); Nouscraft is
  // our own content, not an ad buy. The mobile app has always shown it to
  // everyone, and two platforms disagreeing about what a membership includes is
  // worse than either answer.

  return <div className='sm:hidden w-full h-full flex flex-col gap-y-2'>
    <div ref={impressionRef} className='bg-cccpurple text-white text-xs p-3 flex flex-col gap-y-3 justify-between items-center text-center border border-1 dark:border-white border-gray-900 h-full'>
      <h6 className='text-center font-extrabold dark:text-white'>Nouscraft Audiobook Trailer</h6>

      <div className='w-full max-w-[160px] relative cursor-pointer' onClick={handlePlayAudio}>
        <img className="w-full h-auto rounded-lg border border-2 border-white" src={'https://www.nouscraft.com/images/book1.png'} alt={'Nouscraft'} loading="lazy" />
        <div className='absolute inset-0 flex items-center justify-center'>
          <div className={`bg-black/60 rounded-full p-3 transition-all ${isPlaying ? 'scale-90 opacity-80' : 'hover:scale-110'}`}>
            {isPlaying ? (
              <PauseIcon className='h-8 w-8 text-white' />
            ) : (
              <PlayIcon className='h-8 w-8 text-white' />
            )}
          </div>
        </div>
      </div>
      
      {(duration > 0 || isPlaying) && (
        <div className='w-full px-2'>
          <input
            type='range'
            min='0'
            max={duration || 100}
            value={currentTime}
            onChange={handleSeek}
            className='w-full h-2 bg-white/30 rounded-lg appearance-none cursor-pointer slider'
            style={{
              background: `linear-gradient(to right, #fff 0%, #fff ${(currentTime/duration)*100}%, rgba(255,255,255,0.3) ${(currentTime/duration)*100}%, rgba(255,255,255,0.3) 100%)`
            }}
          />
          <div className='flex justify-between text-xs mt-1'>
            <span>{Math.floor(currentTime / 60)}:{String(Math.floor(currentTime % 60)).padStart(2, '0')}</span>
            <span>{Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, '0')}</span>
          </div>
        </div>
      )}
      
      <a href='https://nouscraft.com' target='_blank' rel='noopener noreferrer' onClick={() => trackAdClick('nouscraft', 'mobile', 'enter-cta')} className="inline-flex justify-center px-4 py-2 text-sm font-medium rounded-md text-white hover:text-white bg-gradient-to-r from-cccblue to-cccorange hover:from-cccorange hover:to-cccblue" >
        <span>Enter Nouscraft</span>
      </a>
    </div>
  </div>
}

const MobileVideomancyAd = () => {
  const [currentUser] = useGlobalState('currentUser')
  if (currentUser && !currentUser.shouldSeeAds) { return null }

  return (
    <div className='sm:hidden w-full flex flex-col gap-y-2'>
      <div className='bg-cccpurple text-white text-xs p-1 flex flex-col gap-y-2 justify-center items-center text-center border border-1 dark:border-white border-gray-900'>
        <h6 className='text-center mb-1 font-extrabold dark:text-white'>Turn Audio into Video </h6>
        <div className='flex flex gap-x-1'>
          <div className='w-36'>
            <div>
              <div style={{position: 'relative', paddingBottom: "177.77777777777777%", height: 0}}><iframe src="https://www.loom.com/embed/6d203c0d3c334fdfab33b8f5f32552a0?sid=84b5a338-6bec-4357-a28a-4d5f21c17a84" frameBorder="0" allowFullScreen style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%'}}></iframe></div>
            </div>
          </div>
        </div>
        <a href={`https://www.videomancy.com?utm_source=ccc&cccu=${currentUser?.id}`} className="inline-flex mr-3 justify-center px-4 py-2 text-sm font-medium rounded-md text-white hover:text-white bg-gradient-to-r from-cccblue to-cccorange hover:from-cccorange hover:to-cccblue" >
          <span>Try Videomancy</span>
        </a>
      </div>
    </div>
  )
}

const MobileEducationAd = () => {
  const [eduAds] = useGlobalState('eduAds')
  const [currentUser] = useGlobalState('currentUser')
  const impressionRef = useAdImpression('closingcredits', 'mobile', 'courses')
  const ads = eduAds?.length > 0 ? eduAds : defaultEduAds

  // Closing Credits (education) promos deliberately show to EVERYONE,
  // including ad-free premium members — owner decision 2026-07-29: paying
  // members are the likeliest course buyers, and this is house content, not
  // a third-party ad buy. The other promo units stay gated on shouldSeeAds.
  if (ads.length === 0) { return null }

  return (
    <div id='ezoic-sidebar-ads' className='sm:hidden w-full flex flex-col gap-y-2'>
      <div ref={impressionRef} className='bg-cccblue text-white text-xs p-1 flex flex-col gap-y-2 justify-center items-center text-center border border-1 dark:border-white border-gray-900'>
        <h6 className='text-center mb-1 font-extrabold dark:text-white'>Now Enrolling </h6>
        <div className='flex flex gap-x-1'>
          <div className='w-36'>
            <div className="aspect-w-16 aspect-h-9">
              <a href={`https://www.closingcredits.com/courses/voice-acting-101?cccu=${currentUser?.id}`} onClick={() => trackAdClick('closingcredits', 'mobile', 'voice-acting-101')}>
                <img className="object-cover w-full rounded-lg border border-2 border-white" src={`https://img.closingcredits.com/image_uploads/64/64va101.png`} alt={'Voice Acting 101'} />
              </a>
            </div>
          </div>

          { ads.map((ad, i) => {
            if (i === 0) { return <div key={`eduad${ad.name}`} className='w-36'>
              <div className="aspect-w-16 aspect-h-9">
                <a href={`https://www.closingcredits.com/courses/${ad.remoteUrl}?cccu=${currentUser?.id}`} onClick={() => trackAdClick('closingcredits', 'mobile', ad.remoteUrl || ad.name)}>
                  <img className="object-cover w-full rounded-lg border border-2 border-white" src={`${ad.imageUrl}`} alt={ad.name} />
                </a>
              </div>
            </div>
          }}) }
        </div>
        <a href='https://www.castingcall.club/links?march=clc' onClick={() => trackAdClick('closingcredits', 'mobile', 'enroll-cta')} className="inline-flex mr-3 justify-center px-4 py-2 text-sm font-medium rounded-md text-white hover:text-white bg-gradient-to-r from-cccpurple to-cccorange hover:from-cccorange hover:to-cccpurple" >
          <span>Reserve Your Seat</span>
        </a>
      </div>
    </div>
  )
}

const SidebarNouscraftAd = () => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [hasTrackedClick, setHasTrackedClick] = useState(false)
  const audioRef = useRef(null)
  const impressionRef = useAdImpression('nouscraft', 'sidebar', 'audiobook-trailer')
  const { putpostRequest } = useQuery()

  const handlePlayAudio = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!audioRef.current) {
      audioRef.current = new Audio('https://images.castingcall.club/nouscraft-commercial.mp3')
      audioRef.current.addEventListener('ended', () => setIsPlaying(false))
      audioRef.current.addEventListener('loadedmetadata', () => {
        setDuration(audioRef.current.duration)
      })
      audioRef.current.addEventListener('timeupdate', () => {
        setCurrentTime(audioRef.current.currentTime)
      })
    }
    
    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      // Track the click only once per session
      if (!hasTrackedClick) {
        putpostRequest('/api/v3/auggers/track_nouscraft_click', 'POST', {}, (err, data) => {
          if (!err) {
            setHasTrackedClick(true)
          }
        })
      }
      trackAdClick('nouscraft', 'sidebar', 'audiobook-trailer')
      audioRef.current.play()
      setIsPlaying(true)
    }
  }

  const handleSeek = (e) => {
    e.stopPropagation()
    const newTime = parseFloat(e.target.value)
    setCurrentTime(newTime)
    if (audioRef.current) {
      audioRef.current.currentTime = newTime
    }
  }

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  // Ad-free members see this too — see MobileNouscraftAd for why.

  return (
    <div className='hidden xl:block min-w-96 max-w-96 flex flex-col gap-y-2'>
      <div ref={impressionRef} className='bg-cccpurple text-white text-xs p-4 flex flex-col gap-y-3 justify-center items-center text-center border border-2 rounded-xl dark:border-white border-gray-900'>
        <h6 className='text-center font-extrabold dark:text-white'>Nouscraft Audiobook Trailer</h6>
        
        <div className='w-full sm:w-72 relative cursor-pointer' onClick={handlePlayAudio}>
          <img className="w-full h-auto rounded-lg border border-2 border-white" src={`https://www.nouscraft.com/images/book1.png`} alt={'Nouscraft'} loading="lazy" />
          <div className='absolute inset-0 flex items-center justify-center'>
            <div className={`bg-black/60 rounded-full p-4 transition-all ${isPlaying ? 'scale-90 opacity-80' : 'hover:scale-110'}`}>
              {isPlaying ? (
                <PauseIcon className='h-12 w-12 text-white' />
              ) : (
                <PlayIcon className='h-12 w-12 text-white' />
              )}
            </div>
          </div>
        </div>
        
        {(duration > 0 || isPlaying) && (
          <div className='w-full'>
            <input
              type='range'
              min='0'
              max={duration || 100}
              value={currentTime}
              onChange={handleSeek}
              className='w-full h-2 bg-white/30 rounded-lg appearance-none cursor-pointer slider'
              style={{
                background: `linear-gradient(to right, #fff 0%, #fff ${(currentTime/duration)*100}%, rgba(255,255,255,0.3) ${(currentTime/duration)*100}%, rgba(255,255,255,0.3) 100%)`
              }}
            />
            <div className='flex justify-between text-xs mt-1'>
              <span>{Math.floor(currentTime / 60)}:{String(Math.floor(currentTime % 60)).padStart(2, '0')}</span>
              <span>{Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, '0')}</span>
            </div>
          </div>
        )}
        
        <a href='https://nouscraft.com' target='_blank' rel='noopener noreferrer' onClick={() => trackAdClick('nouscraft', 'sidebar', 'enter-cta')} className="inline-flex justify-center px-4 py-2 text-sm font-medium rounded-md text-white hover:text-white bg-gradient-to-r from-cccblue to-cccorange hover:from-cccorange hover:to-cccblue" >
          <span>Enter Nouscraft</span>
        </a>
      </div>
    </div>
  )
}

const SidebarVideomancyAd = () => {
  const [currentUser] = useGlobalState('currentUser')
  if (currentUser && !currentUser.shouldSeeAds) { return null }
  return (
    <div className='hidden xl:block min-w-96 max-w-96 flex flex-col gap-y-2'>
      <div className='bg-cccpurple text-white text-xs p-1 flex flex-col gap-y-2 justify-center items-center text-center border border-1 dark:border-white border-gray-900'>
        <h6 className='text-center mb-1 font-extrabold dark:text-white'>Turn Audio Into Video!</h6>
        <div className='flex flex-col gap-y-1'>
          <div className='w-full sm:w-72'>
            <div>
              <div style={{position: 'relative', paddingBottom: "177.77777777777777%", height: 0}}><iframe src="https://www.loom.com/embed/6d203c0d3c334fdfab33b8f5f32552a0?sid=84b5a338-6bec-4357-a28a-4d5f21c17a84" frameBorder="0"   allowFullScreen style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%'}}></iframe></div>
            </div>
          </div>
        </div>
        <a href={`https://www.videomancy.com?utm_source=ccc&cccu=${currentUser?.id}`} className="inline-flex mr-3 justify-center px-4 py-2 text-sm font-medium rounded-md text-white hover:text-white bg-gradient-to-r from-cccblue to-cccorange hover:from-cccorange hover:to-cccblue" >
          <span>Try Videomancy</span>
        </a>
      </div>
    </div>
  )
}

const SidebarEducationAd = () => {
  const [eduAds] = useGlobalState('eduAds')
  const [currentUser] = useGlobalState('currentUser')
  const impressionRef = useAdImpression('closingcredits', 'sidebar', 'courses')
  const ads = eduAds?.length > 0 ? eduAds : defaultEduAds

  // Closing Credits (education) promos deliberately show to EVERYONE,
  // including ad-free premium members — owner decision 2026-07-29: paying
  // members are the likeliest course buyers, and this is house content, not
  // a third-party ad buy. The other promo units stay gated on shouldSeeAds.
  if (ads.length === 0) { return null }

  return (
    <div id='ezoic-sidebar-ads' className='hidden xl:block min-w-96 max-w-96 flex flex-col gap-y-2'>
      <div ref={impressionRef} className='bg-cccblue text-white text-xs p-1 flex flex-col gap-y-2 justify-center items-center text-center border border-2 rounded-xl dark:border-white border-gray-900'>
        <h6 className='text-center mb-1 font-extrabold dark:text-white'>Now Enrolling </h6>
        <div className='flex flex-col gap-y-1'>
          <div className='w-full sm:w-72'>
            <div className="aspect-w-16 aspect-h-9">
              <a href={`https://www.closingcredits.com/courses/voice-acting-101?cccu=${currentUser?.id}`} onClick={() => trackAdClick('closingcredits', 'sidebar', 'voice-acting-101')}>
                <img className="object-cover w-full rounded-lg border border-2 border-white" src={`https://img.closingcredits.com/image_uploads/64/64va101.png`} alt={'Voice Acting 101'} />
              </a>
            </div>
          </div>
          { ads.map((ad, i) => <div key={`sidebareduad${ad.name}`} className='w-full sm:w-72'>
            <div className="aspect-w-16 aspect-h-9">
              <a href={`https://www.closingcredits.com/courses/${ad.remoteUrl}?cccu=${currentUser?.id}`} onClick={() => trackAdClick('closingcredits', 'sidebar', ad.remoteUrl || ad.name)}>
                <img className="object-cover w-full rounded-lg border border-2 border-white" src={`${ad.imageUrl}`} alt={ad.name} />
              </a>
            </div>
          </div>)}
        </div>
        <a href='https://www.castingcall.club/links?march=clc' onClick={() => trackAdClick('closingcredits', 'sidebar', 'enroll-cta')} className="inline-flex mr-3 justify-center px-4 py-2 text-sm font-medium rounded-md text-white hover:text-white bg-gradient-to-r from-cccpurple to-cccorange hover:from-cccorange hover:to-cccpurple" >
          <span>Reserve Your Seat</span>
        </a>
      </div>
    </div>
  )
}

// Renders the placeholder only. The ad request/teardown is owned by
// useEzoicAds() in App.jsx, which batches one showAds per route commit.
const SidebarAd = ({ adID }) => {
  const [currentUser] = useGlobalState('currentUser')

  // Ask for a request on mount. Pages that render slots after an async fetch
  // (profile, dashboard, search) mount long after the route hook fired, so
  // without this their slots are never requested. Debounced + idempotent.
  useEffect(() => { requestEzoicAds() }, [])

  return <>
    { currentUser?.shouldSeeAds && <div id='ezoic-sidebar-ads' className='hidden xl:block min-w-96 max-w-96 flex flex-col gap-y-2'>
    <div className='bg-cccpurple text-white text-xs p-1 flex flex-col gap-y-2 justify-center items-center text-center border border-1 dark:border-white border-gray-900'>
      <img src='https://images.castingcall.club/ccc-premium.svg' className='w-48' />
      <span>Get ad-free experience with special benefits and directly support CCC.</span>
      <a href='/pricing' className="inline-flex justify-center px-4 py-2 shadow-sm text-sm text-white font-medium rounded-md bg-gradient-to-r from-cccblue to-cccorange hover:from-cccorange hover:to-cccblue hover:text-white"> Get CCC Premium </a>
    </div>
    <div className='w-96' id={`ezoic-pub-ad-placeholder-${adID}`} />
  </div> }
  </>
}

// Placeholder only — see SidebarAd; useEzoicAds() in App.jsx owns the request.
const MobileAd = ({ adID }) => {
  const [currentUser] = useGlobalState('currentUser')

  // Ask for a request on mount. Pages that render slots after an async fetch
  // (profile, dashboard, search) mount long after the route hook fired, so
  // without this their slots are never requested. Debounced + idempotent.
  useEffect(() => { requestEzoicAds() }, [])

  return <>
    {currentUser?.shouldSeeAds && <div id='ezoic-mobile-ad' className='sm:hidden w-full flex flex-col gap-y-2'>
    <div className='bg-cccpurple text-white text-xs p-1 flex flex-col gap-y-2 justify-center items-center text-center border border-1 dark:border-white border-gray-900'>
      <img src='https://images.castingcall.club/ccc-premium.svg' className='w-48' />
      <span>Get ad-free experience with special benefits and directly support CCC.</span>
      <a href='/pricing' className="inline-flex justify-center px-4 py-2 shadow-sm text-sm text-white font-medium rounded-md bg-gradient-to-r from-cccblue to-cccorange hover:from-cccorange hover:to-cccblue hover:text-white"> Get CCC Premium </a>
    </div>
    <div className='w-full mt-2 md:hidden'>
      <div id={`ezoic-pub-ad-placeholder-${adID}`} />
    </div>
  </div> }
  </>
}

// Id-less in-content ad for SPA pages. Unlike SidebarAd (xl only) and MobileAd
// (mobile only), this covers desktop, tablet AND mobile via per-div device
// targeting, so it fills the tablet/small-laptop gap those two leave open. It
// uses class-based `.ezoicad` placements (no dashboard IDs) — the same approach
// as the server-rendered pages — so it also inherits the dev-mode outline.
// Self-gates on shouldSeeAds to respect ad-free members. Placeholder only: the
// matching showAds('.ezoicad') is issued once per route by useEzoicAds().
const InContentAd = () => {
  const [currentUser] = useGlobalState('currentUser')
  const shouldSeeAds = currentUser?.shouldSeeAds

  // Ask for a request on mount. Pages that render slots after an async fetch
  // (profile, dashboard, search) mount long after the route hook fired, so
  // without this their slots are never requested. Debounced + idempotent.
  useEffect(() => { requestEzoicAds() }, [])

  if (!shouldSeeAds) return null

  // data-fluid="false" blocks variable-height native creatives, and each slot
  // declares one predictable height per breakpoint, so layouts/_ad_stability
  // can reserve exactly the right box and nothing ever shifts.
  return <div className='mt-4'>
    <div className='ezoicad' data-devices='desktop,tablet' data-sizes='300x250' data-fluid='false' />
    <div className='ezoicad' data-devices='mobile' data-sizes='300x250,336x280' data-fluid='false' />
  </div>
}

// In-feed unit for result lists (project/talent search). One div serves both
// breakpoints via data-mobile_sizes/data-desktop_sizes.
//
// Placeholder only. The matching showAds('.ezoicad') is issued by the list that
// renders these, after appended results commit — the route-level hook in
// App.jsx can't see in-page appends (infinite scroll, filter changes).
const InFeedAd = () => {
  const [currentUser] = useGlobalState('currentUser')
  // No currentUser means an anonymous visitor — this list also renders on the
  // logged-out search page — and they should see ads. Only ad-free paid members
  // opt out. (SidebarAd/MobileAd gate on currentUser?.shouldSeeAds, which is
  // why they render nothing when logged out.)
  const shouldSeeAds = currentUser ? currentUser.shouldSeeAds : true

  // Ask for a request on mount. Pages that render slots after an async fetch
  // (profile, dashboard, search) mount long after the route hook fired, so
  // without this their slots are never requested. Debounced + idempotent.
  useEffect(() => { requestEzoicAds() }, [])

  if (!shouldSeeAds) return null

  // One predictable height per breakpoint (desktop is leaderboard-only rather
  // than 728x90 OR 300x250, which would vary the box by 160px) so the reserved
  // space in layouts/_ad_stability always matches what renders.
  //
  // NOT data-required='false' anymore. Skippable slots were left unreserved and
  // filled mid-scroll, which visibly jumped the feed — and no-layout-shift is
  // an owner hard rule (2026-07-28) that outranks Ezoic's density optimizer.
  // Every in-feed slot now owns a reserved box (see layouts/_ad_stability) and
  // is always requested; the worst case is a blank box, never a jump.
  return <div
    className='ezoicad'
    data-fluid='false'
    data-mobile_sizes='300x250,336x280'
    data-desktop_sizes='728x90'
  />
}

const MobileAdBundle = ({ezoicAdId}) => {
  return <div className='flex flex-col gap-y-1'>
    <div className='grid grid-cols-2 gap-x-1 sm:hidden'>
      <MobileUserAd />
      <MobileNouscraftAd />
    </div>
    <MobileAd adID={ezoicAdId} />
    <MobileEducationAd />
  </div>
}

const MobileUserAd = () => {
  const [ad, setAd] = useState(null)
  const [loading, setLoading] = useState(true)
  const { getRequest, putpostRequest } = useQuery()
  const impressionRef = useAdImpression('member-promo', 'mobile', ad ? `promo-${ad.id}` : 'house-fallback')

  useEffect(() => {
    fetchRandomSquareAd()
  }, [])

  const fetchRandomSquareAd = async () => {
    try {
      getRequest('/api/v3/promotions/random_square', {}, (err, data) => {
        if (!err && data?.ad) {
          setAd(data.ad)
        }
        setLoading(false)
      })
    } catch (error) {
      setLoading(false)
    }
  }

  const handleAdClick = () => {
    if (ad?.url) {
      // Track the click
      putpostRequest(`/api/v3/promotions/${ad.id}/click`, 'POST', {}, () => {})
      trackAdClick('member-promo', 'mobile', `promo-${ad.id}`)
      // Open the ad URL
      window.open(ad.url, '_blank', 'noopener,noreferrer')
    }
  }

  if (loading) {
    return null
  }

  if (!ad) {
    // Compact placeholder sized to match MobileNouscraftAd when shown
    // side-by-side in MobileAdBundle. Single icon + title + short tagline
    // + button — no bullet list, no decorative blobs.
    return (
      <div className='sm:hidden w-full h-full flex flex-col gap-y-2'>
        <div ref={impressionRef} className='bg-gradient-to-br from-cccorange via-cccpurple to-cccblue text-white p-3 flex-1 flex flex-col gap-y-3 justify-between items-center text-center border border-1 dark:border-white border-gray-900'>
          <h6 className='text-center font-extrabold text-white'>Your Ad Here!</h6>
          <div className='relative'>
            <SpeakerphoneIcon className='h-10 w-10 text-white' />
            <SparklesIcon className='h-5 w-5 text-yellow-300 absolute -top-1 -right-1' />
          </div>
          <p className='text-xs leading-snug text-white/90'>
            Reach thousands of creative professionals
          </p>
          <a
            href='/advertising'
            className='inline-flex items-center gap-2 px-3 py-1.5 bg-white text-cccorange font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200 shadow-md text-sm'
          >
            <SparklesIcon className='h-3 w-3' />
            <span>Advertise</span>
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className='sm:hidden w-full h-full flex flex-col gap-y-2'>
      <div ref={impressionRef} className='bg-cccorange text-white text-xs p-3 flex-1 flex flex-col gap-y-3 justify-between items-center text-center border border-1 dark:border-white border-gray-900'>
        <h6 className='text-center font-extrabold dark:text-white'>CCC Member Promotion</h6>
        <div className='flex flex gap-x-1'>
          <div className='w-36'>
            <div 
              className='cursor-pointer hover:opacity-90 transition-opacity'
              onClick={handleAdClick}
              title={`Visit ${ad.name}`}
            >
              <div className="aspect-w-1 aspect-h-1">
                <img
                  src={ad.image_url}
                  alt={ad.name}
                  className="object-cover w-full rounded-lg border border-2 border-white"
                  onError={(e) => {
                    e.target.style.display = 'none'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <a 
          href='/advertising/campaigns' 
          className='inline-flex items-center gap-2 px-3 py-1.5 bg-white text-cccorange font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200 shadow-md text-sm'
        >
          <SparklesIcon className='h-3 w-3' />
          <span>Your Ad Here</span>
        </a>
      </div>
    </div>
  )
}

const SidebarUserAd = () => {
  const [ad, setAd] = useState(null)
  const [loading, setLoading] = useState(true)
  const { getRequest, putpostRequest } = useQuery()
  const impressionRef = useAdImpression('member-promo', 'sidebar', ad ? `promo-${ad.id}` : 'house-fallback')

  useEffect(() => {
    fetchRandomSquareAd()
  }, [])

  const fetchRandomSquareAd = async () => {
    try {
      getRequest('/api/v3/promotions/random_square', {}, (err, data) => {
        if (!err && data?.ad) {
          setAd(data.ad)
        }
        setLoading(false)
      })
    } catch (error) {
      setLoading(false)
    }
  }

  const handleAdClick = () => {
    if (ad?.url) {
      // Track the click
      putpostRequest(`/api/v3/promotions/${ad.id}/click`, 'POST', {}, () => {})
      trackAdClick('member-promo', 'sidebar', `promo-${ad.id}`)
      // Open the ad URL
      window.open(ad.url, '_blank', 'noopener,noreferrer')
    }
  }

  if (loading) {
    return null
  }

  if (!ad) {
    // Beautiful placeholder when no ad is available
    return (
      <div className='hidden xl:block min-w-96 max-w-96 flex flex-col gap-y-2'>
        <div ref={impressionRef} className='bg-gradient-to-br from-cccorange via-cccpurple to-cccblue text-white p-6 flex flex-col gap-y-4 justify-center items-center text-center border border-2 rounded-xl dark:border-white border-gray-900 shadow-lg relative overflow-hidden'>
          {/* Decorative background pattern */}
          <div className='absolute inset-0 opacity-10'>
            <div className='absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16' />
            <div className='absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-x-8 translate-y-8' />
          </div>
          
          <div className='relative '>
            <div className='flex justify-center mb-3'>
              <div className='relative'>
                <SpeakerphoneIcon className='h-12 w-12 text-white' />
                <SparklesIcon className='h-6 w-6 text-yellow-300 absolute -top-1 -right-1' />
              </div>
            </div>
            
            <h6 className='text-lg font-extrabold text-white mb-2'>Your Ad Here!</h6>
            
            <div className='space-y-3'>
              <p className='text-sm leading-relaxed text-white/90'>
                Reach millions of talented voice actors, directors, and creative professionals.
              </p>
              
              <div className='bg-white/20 backdrop-blur-sm rounded-lg p-3'>
                <ul className='text-xs space-y-1 text-left'>
                  <li className='flex items-center gap-1'>
                    <span className='text-yellow-300'>✓</span> 
                    <span>250x250 pixel ad space</span>
                  </li>
                  <li className='flex items-center gap-1'>
                    <span className='text-yellow-300'>✓</span> 
                    <span>Sidebar placement</span>
                  </li>
                  <li className='flex items-center gap-1'>
                    <span className='text-yellow-300'>✓</span> 
                    <span>Promote your project or profile</span>
                  </li>
                </ul>
              </div>
              
              <a 
                href='/advertising' 
                className='inline-flex items-center gap-2 px-4 py-2 bg-white text-cccorange font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200 shadow-md'
              >
                <SparklesIcon className='h-4 w-4' />
                <span>Advertise Now</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='hidden xl:block min-w-96 max-w-96 flex flex-col gap-y-2'>
      <div ref={impressionRef} className='bg-cccorange text-white text-xs p-1 flex flex-col gap-y-2 justify-center items-center text-center border border-2 rounded-xl dark:border-white border-gray-900'>
        <h6 className='text-center mb-1 font-extrabold dark:text-white'>CCC Member Promotion</h6>
        <div className='hidden xl:block min-w-96 max-w-96 flex flex-col gap-y-2'>
          <div 
            className='cursor-pointer hover:opacity-90 transition-opacity'
            onClick={handleAdClick}
            title={`Visit ${ad.name}`}
          >
            <div 
              className="border-2 border-gray-300 dark:border-gray-600 rounded overflow-hidden bg-white dark:bg-gray-700"
              style={{ 
                width: '250px', 
                height: '250px',
                margin: '0 auto'
              }}
            >
              <img
                src={ad.image_url}
                alt={ad.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
            </div>
          </div>
        </div>
        <a 
          href='/advertising/campaigns' 
          className='inline-flex items-center gap-2 px-4 py-2 bg-white text-cccorange font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200 shadow-md'
        >
          <SparklesIcon className='h-4 w-4' />
          <span>Your Ad Here</span>
        </a>
      </div>
    </div>
  )
}

const SidebarAdBundle = ({ezoicAdId}) => {
  return <div className='flex flex-col gap-y-1'>
    <SidebarUserAd />
    <SidebarNouscraftAd />
    <SidebarEducationAd />
    <SidebarAd adID={ezoicAdId} />
  </div>
}

MobileAd.propTypes = {
  adID: PropTypes.number.isRequired
}

SidebarAd.propTypes = {
  adID: PropTypes.number.isRequired
}

export { SidebarAd, MobileVideomancyAd, MobileAd, InContentAd, InFeedAd, SidebarEducationAd, MobileEducationAd, ProjectAds, SidebarVideomancyAd, MobileAdBundle, SidebarAdBundle, SidebarUserAd, MobileUserAd }
